import {useEffect, useMemo, useRef, useState, type CSSProperties} from 'react'
import {useAppSelector} from '../../../common/store'
import {selectActiveScene} from '../../../common/components/store/scenesSlice'
import {useExportFramePreview} from '../../../common/components/exportFramePreviewStore'
import {carouselGapPx} from '../../../common/constants/exportPresets'

// ── ExportFrameOverlay — darkens area outside the export frame ────

const ExportFrameOverlay = () => {
  const exportWidth = useAppSelector((s) => selectActiveScene(s).exportWidth)
  const exportHeight = useAppSelector((s) => selectActiveScene(s).exportHeight)
  const exportFrameEnabled = useAppSelector((s) => selectActiveScene(s).exportFrameEnabled)
  const carouselEnabled = useAppSelector((s) => selectActiveScene(s).exportCarouselEnabled)
  const carouselCount = useAppSelector((s) => selectActiveScene(s).exportCarouselCount)
  const carouselGap = useAppSelector((s) => selectActiveScene(s).exportCarouselGap)
  const previewActive = useExportFramePreview()

  // Effective slice count — 1 when carousel disabled, else the count picker value.
  const slices = carouselEnabled ? carouselCount : 1
  // Gutter between slices (the discarded App Store gallery spacing) in export px.
  const gapPx = carouselGapPx(exportWidth, carouselGap)
  const totalWidth = exportWidth * slices + gapPx * (slices - 1)
  const aspect = totalWidth / exportHeight

  // Compute the frame size to "contain" inside the viewport while preserving aspect.
  // CSS `aspect-ratio` + `height:100%` + `max-width:100%` breaks down at wide aspects
  // (e.g. 5:1 for a 5-frame square carousel) because once both width AND height are
  // constrained, CSS ignores aspect-ratio and the box collapses to viewport shape.
  //
  // `isVisible` is in the dep list because the JSX (and `rootRef`'s DOM node) only
  // exists when the overlay is being shown. Without it, the effect runs once on
  // mount with `rootRef.current === null` and never re-attaches the observer when
  // the user later toggles the frame on — the frame would render at 0×0.
  const rootRef = useRef<HTMLDivElement>(null)
  const [frame, setFrame] = useState({width: 0, height: 0})
  const isVisible = exportFrameEnabled || previewActive
  useEffect(() => {
    if (!isVisible) return
    const el = rootRef.current
    if (!el) return
    const update = () => {
      const vpW = el.clientWidth
      const vpH = el.clientHeight
      if (vpW === 0 || vpH === 0) return
      const widthIfHeightFits = vpH * aspect
      if (widthIfHeightFits <= vpW) setFrame({width: widthIfHeightFits, height: vpH})
      else setFrame({width: vpW, height: vpW / aspect})
    }
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [aspect, isVisible])

  const frameStyle: CSSProperties = useMemo(
    () => ({
      width: `${frame.width}px`,
      height: `${frame.height}px`,
      // Punch through with box-shadow darkening surrounding area + a soft
      // outer glow so the frame perimeter reads against busy backgrounds.
      boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.55), 0 0 0 1px rgba(255, 255, 255, 0.18)',
      backgroundColor: 'transparent',
      border: '2px solid rgba(255, 255, 255, 0.85)',
      position: 'relative',
    }),
    [frame.width, frame.height],
  )

  if (!isVisible) return null

  // N-1 internal seams. With a gap, each seam is a darkened GUTTER band spanning
  // the discarded region between slice i-1 and slice i (so the user sees what
  // won't be rendered); with no gap it collapses to a thin divider line.
  const seams: {leftPct: number; widthPct: number}[] = []
  for (let i = 1; i < slices; i++) {
    const gutterStart = i * (exportWidth + gapPx) - gapPx
    seams.push({leftPct: (gutterStart / totalWidth) * 100, widthPct: (gapPx / totalWidth) * 100})
  }

  return (
    <div ref={rootRef} style={rootStyle}>
      <div style={frameStyle}>
        {seams.map((s, i) =>
          s.widthPct > 0 ? (
            <div key={i} style={{...gutterStyle, left: `${s.leftPct}%`, width: `${s.widthPct}%`}} />
          ) : (
            <div key={i} style={{...dividerStyle, left: `${s.leftPct}%`}} />
          ),
        )}
      </div>
    </div>
  )
}

// ── Static styles ─────────────────────────────────────────────────

const rootStyle: CSSProperties = {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  pointerEvents: 'none',
  // Above TextOverlay (z 6) so the darkened margins + gutter bands dim 2D
  // text/sprites that fall outside the frame or into a gutter — a truer preview
  // of what actually exports. Stays below the export-hide overlay (8),
  // loading (10) and toolbars (20).
  zIndex: 7,
  overflow: 'hidden',
}

const dividerStyle: CSSProperties = {
  position: 'absolute',
  top: 0,
  bottom: 0,
  width: 0,
  // Bright solid line + subtle dark halo so each slice border reads
  // clearly even against light or busy scene backgrounds.
  borderLeft: '2px solid rgba(255, 255, 255, 0.9)',
  boxShadow: '0 0 0 1px rgba(0, 0, 0, 0.35)',
  pointerEvents: 'none',
}

const gutterStyle: CSSProperties = {
  position: 'absolute',
  top: 0,
  bottom: 0,
  // Darkened band with bright edges marks the discarded gutter (App Store gap):
  // content here is never rendered into a slice.
  backgroundColor: 'rgba(0, 0, 0, 0.45)',
  borderLeft: '1px solid rgba(255, 255, 255, 0.85)',
  borderRight: '1px solid rgba(255, 255, 255, 0.85)',
  pointerEvents: 'none',
}

export default ExportFrameOverlay
